import {
  PRODUCT_LIST_SEARCHES_LOADED,
  PRODUCT_LIST_SEARCHES_CLEAR,
  PRODUCT_LIST_SEARCHES_ADDED_LOADED,
  PRODUCT_LIST_SEARCHES_ADDED_CLEAR,
  PRODUCT_LIST_SEARCHES_META_LOADED,
  PRODUCT_LIST_SEARCHES_META_CLEAR,
  PRODUCT_LIST_SEARCHES_STATS_LOADED,
  PRODUCT_LIST_SEARCHES_STATS_CLEAR,
} from '../../actionTypes';

import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';
import { setFaviconLoading, setFaviconDefault } from '../../faviconActions';

// Reducer corresponding actions
function listProductSearchesClear() {
  return { type: PRODUCT_LIST_SEARCHES_CLEAR };
}

function listProductSearchesLoaded(searches) {
  return {
    type: PRODUCT_LIST_SEARCHES_LOADED,
    payload: { searches },
  };
}

function listProductSearchesAddedClear() {
  return { type: PRODUCT_LIST_SEARCHES_ADDED_CLEAR };
}

function listProductSearchesAddedLoaded(addedSearch) {
  return {
    type: PRODUCT_LIST_SEARCHES_ADDED_LOADED,
    payload: { addedSearch },
  };
}

function listProductSearchesMetaClear() {
  return { type: PRODUCT_LIST_SEARCHES_META_CLEAR };
}

function listProductSearchesMetaLoaded(meta) {
  return {
    type: PRODUCT_LIST_SEARCHES_META_LOADED,
    payload: { meta },
  };
}

function listProductSearchesStatsClear() {
  return { type: PRODUCT_LIST_SEARCHES_STATS_CLEAR };
}

function listProductSearchesStatsLoaded(stats) {
  return {
    type: PRODUCT_LIST_SEARCHES_STATS_LOADED,
    payload: { stats },
  };
}

// Constants for filter mapping
const SCOPE_TO_STATUS_MAP = {
  live: 'running',
  paused: 'paused',
  scheduled: 'scheduled',
  completed: 'completed',
  canceled: 'canceled',
  failed: 'failed',
};

const GTE_PREDICATES = ['gte', 'gteq'];
const LTE_PREDICATES = ['lte', 'lteq'];

function mapParamsToSearchService(params) {
  /* eslint-disable camelcase */
  const mapped = {};

  // Pagination
  if (params.paging) {
    mapped.page = params.paging.to || 1;
    mapped.page_size = params.paging.size || 10;
  }

  // Scope → Status mapping
  if (params.scope && SCOPE_TO_STATUS_MAP[params.scope]) {
    mapped.status = SCOPE_TO_STATUS_MAP[params.scope];
  }

  // Sorting
  if (params.sort) {
    const sortBy = params.sort.name;
    // Convert camelCase to snake_case (startDate → start_date)
    mapped.sort_by = sortBy?.replace(/([A-Z])/g, '_$1').toLowerCase();
    mapped.sort_direction = params.sort.direction || 'DESC';
  }

  // Filters (array)
  if (params.filters && Array.isArray(params.filters)) {
    params.filters.forEach((filter) => {
      const { name, value, predicate } = filter;

      // Direct mapping filters (no predicate needed)
      const directMappings = {
        status: 'status',
        name: 'name',
        unit: 'unit',
      };

      if (directMappings[name]) {
        mapped[directMappings[name]] = value;
        return;
      }

      // Platform filter (ensure array)
      if (name === 'platform' || name === 'platforms') {
        mapped.platforms = Array.isArray(value) ? value : [value];
        return;
      }

      // Range filters (start_date, end_date, result_count)
      const rangeFilters = ['start_date', 'end_date', 'result_count'];

      if (rangeFilters.includes(name)) {
        if (GTE_PREDICATES.includes(predicate)) {
          mapped[`${name}_gte`] = value;
        } else if (LTE_PREDICATES.includes(predicate)) {
          mapped[`${name}_lte`] = value;
        }
      }
    });
  }

  return mapped;
  /* eslint-enable camelcase */
}

// API
async function getProductListSearchesApi(args, token, isCSV) {
  if (isCSV) {
    // CSV export not yet supported in Search Service - keep using Rails
    await api.getCSV({
      path: 'searches',
      params: args,
      token,
      version: 'v3',
      filename: 'redflag-searches-export',
    });
    return null;
  }

  // Map params to Search Service format
  const searchServiceParams = mapParamsToSearchService(args);

  // Use api.get (flat params) instead of nestedParamsGet (nested params with brackets)
  const response = await api.get({
    path: 'searches',
    params: searchServiceParams,
    token,
    version: 'gateway',
  });
  const searchServiceData = await response.json();

  return searchServiceData;
}

async function getProductListSearchesAddedApi(args, token) {
  // This endpoint gets recently added searches for the banner
  // It uses scope='added' which maps to a specific query
  const searchServiceParams = mapParamsToSearchService(args);

  // Use api.get for flat params
  const response = await api.get({
    path: 'searches',
    params: searchServiceParams,
    token,
    version: 'gateway',
  });
  const searchServiceData = await response.json();
  return searchServiceData;
}

async function getProductListSearchesMetaApi(args, token) {
  const response = await api.get({
    path: 'searches/meta',
    params: args,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function getProductListSearchesStatsApi(args, token) {
  const response = await api.get({
    path: 'searches/stats',
    params: args,
    token,
    version: 'gateway',
  });
  return response.json();
}

// helper
async function fetchProductListSearches(dispatch, args, token, isCSV) {
  try {
    const response = await getProductListSearchesApi(args, token, isCSV);

    if (!isCSV) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(listProductSearchesLoaded(response));
    }

    if (isCSV) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      if (!isCSV) dispatch(setFetchLoaderReset());
      if (isCSV) dispatch(setSubmissionLoaderReset());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchProductListSearchesAdded(dispatch, args, token) {
  try {
    const response = await getProductListSearchesAddedApi(args, token);

    dispatch(listProductSearchesAddedLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchProductListSearchesMeta(dispatch, args, token) {
  try {
    const response = await getProductListSearchesMetaApi(args, token);

    dispatch(setFaviconDefault());
    dispatch(listProductSearchesMetaLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchProductListSearchesStats(dispatch, args, token) {
  try {
    const response = await getProductListSearchesStatsApi(args, token);

    dispatch(setFaviconDefault());
    dispatch(listProductSearchesStatsLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

// Public components actions
export function clearProductListSearchesRequest() {
  return (dispatch) => {
    dispatch(listProductSearchesClear());
  };
}

export function initiateProductListSearchesRequest(args, isCSV = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    if (!dataLoading && !isCSV) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isCSV) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isCSV) {
      dispatch(setFaviconLoading());
    }

    await fetchProductListSearches(dispatch, args, token, isCSV);
  };
}

export function clearProductListSearchesAddedRequest() {
  return (dispatch) => {
    dispatch(listProductSearchesAddedClear());
  };
}

export function initiateProductListSearchesAddedRequest() {
  return async (dispatch, getState) => {
    const { auth: { token } } = getState();
    const args = { scope: 'added' };

    await fetchProductListSearchesAdded(dispatch, args, token);
  };
}

export function clearProductListSearchesMetaRequest() {
  return (dispatch) => {
    dispatch(listProductSearchesMetaClear());
  };
}

export function initiateProductListSearchesMetaRequest(args) {
  return async (dispatch, getState) => {
    const { auth: { token }, favicon: { faviconStatus } } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchProductListSearchesMeta(dispatch, args, token);
  };
}

export function clearProductListSearchesStatsRequest() {
  return (dispatch) => {
    dispatch(listProductSearchesStatsClear());
  };
}

export function initiateProductListSearchesStatsRequest(args) {
  return async (dispatch, getState) => {
    const { auth: { token }, favicon: { faviconStatus } } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchProductListSearchesStats(dispatch, args, token);
  };
}
